"use client";

import { TLeaderBoardRecentUsersResponseDTO } from "@/app/api/leaderboard/users/leaderboard-users.dto";
import { LEADERBOARD_USERS_API } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import LeadersCarouselCard from "./leaders-carousel-card";

import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

type CarouselProps = {
	className?: string;
	country?: string[];
};

const getRecentLeaderboardFounders = async (country?: string[]) => {
	try {
		const url = country?.length
			? `${LEADERBOARD_USERS_API}?country=${encodeURIComponent(country.join(","))}`
			: LEADERBOARD_USERS_API;
		const response = await fetch(url);
		const { data } = (await response.json()) as TLeaderBoardRecentUsersResponseDTO;
		return data;
	} catch (error) {
		return [];
	}
};

const LeadersCarousel = ({ className, country }: CarouselProps) => {
	const { isLoading, data: users = [] } = useQuery({
		queryKey: ["leaderboard-recent-founders", country],
		queryFn: () => getRecentLeaderboardFounders(country),
		refetchOnWindowFocus: false,
	});

	const commonSliderSettings = {
		infinite: true,
		slidesToShow: 2,
		slidesToScroll: 1,
		variableWidth: true,
		autoplay: true,
		speed: 6000,
		autoplaySpeed: 2000,
		cssEase: "linear",
		useCss: true,
		pauseOnHover: false,
		pauseOnFocus: false,
	};
	const topSliderSettings = {
		...commonSliderSettings,
	};
	const bottomSliderSettings = {
		...commonSliderSettings,
		rtl: true,
	};

	// Show up to 20 users: 10 in first row, 10 in second row
	const [firstRowUsers, secondRowUsers] =
		users.length > 10 ? [users.slice(0, 10), users.slice(10, 20)] : [users, undefined];

	return isLoading ? (
		<LeadersCarouselLoading />
	) : (
		<div className="w-full space-y-4">
			<div className={cn("no-scrollbar flex gap-2 overflow-x-scroll", className)}>
				<Slider {...topSliderSettings}>
					{firstRowUsers.map((user) => (
						<div className="p-1" key={user.id}>
							<LeadersCarouselCard
								name={user.name}
								image={user.image || ""}
								isFounder={user.isFounder}
								isVerified={user.isVerified}
								company={user.company || ""}
								username={user.username || ""}
								isAmbassador={user.isAmbassador}
								jobTitle={user.jobTitle || ""}
								howDoYouIdentifyYourself={user.howDoYouIdentifyYourself || ""}
							/>
						</div>
					))}
				</Slider>
			</div>

			{secondRowUsers && (
				<div className={cn("no-scrollbar flex gap-2 overflow-x-scroll", className)}>
					<Slider {...bottomSliderSettings}>
						{secondRowUsers.map((user) => (
							<div className="p-1" key={user.id}>
								<LeadersCarouselCard
									name={user.name}
									image={user.image || ""}
									isFounder={user.isFounder}
									isVerified={user.isVerified}
									company={user.company || ""}
									username={user.username || ""}
									isAmbassador={user.isAmbassador}
									jobTitle={user.jobTitle || ""}
									howDoYouIdentifyYourself={user.howDoYouIdentifyYourself || ""}
								/>
							</div>
						))}
					</Slider>
				</div>
			)}
		</div>
	);
};

const LeadersCarouselLoading = () => {
	return (
		<div className="w-full space-y-4">
			<div className="no-scrollbar flex gap-2 overflow-x-scroll">
				{[...Array(10)].map((_, index) => (
					<Skeleton key={index} count={1} className="h-20 w-64 rounded-lg" />
				))}
			</div>

			<div className="no-scrollbar flex gap-2 overflow-x-scroll">
				{[...Array(10)].map((_, index) => (
					<Skeleton key={index} count={1} className="h-20 w-64 rounded-lg" />
				))}
			</div>
		</div>
	);
};

export default LeadersCarousel;
